import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { buildApiUrl } from '../../../services/apiConfig';

const UserPreferencesSettings = () => {
  const [preferences, setPreferences] = useState({
    llm_model: 'gpt-4o-mini',
    cache_ttl: 3600,
    max_cache: 15,
    api_key: '',
    llm_provider: 'openai',
    host_provider: 'openai-api'
  });
  const [loading, setLoading] = useState(false);
  const [fetchingPreferences, setFetchingPreferences] = useState(true);

  const llmOptions = [
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
    { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' }
  ];

  const llmProviders = [
    { value: 'openai', label: 'OpenAI' },
    { value: 'google', label: 'Google' }
  ];
  const hostProviders = [
    { value: 'openai-api', label: 'OpenAI API' },
    { value: 'google-ai-studio', label: 'Google AI Studio' },
    { value: 'azure-openai', label: 'Azure OpenAI Service' },
    { value: 'vertex-ai', label: 'Google Vertex AI' }
  ];

  const getUserId = () => {
    return sessionStorage.getItem('user_id');

  };

  useEffect(() => {
    const fetchUserPreferences = async () => {
      try {
        const userId = getUserId();
        if (!userId) {
          toast.error('User ID not found in session');
          setFetchingPreferences(false);
          return;
        }

        const response = await axios.get(buildApiUrl('SQL', '/api/user/user_preferences'), {
          params: { user_id: userId }
        });

        if (response.data) {
          setPreferences({
            llm_model: response.data.llm_model || 'gpt-4o-mini',
            cache_ttl: response.data.cache_ttl || 3600,
            max_cache: response.data.max_cache || 15,
            api_key: response.data.api_key || '',
            llm_provider: 'openai',
            host_provider: 'openai-api'
          });
        }
      } catch (error) {
        console.error('Error fetching user preferences:', error);
        // If it's a 404, it might mean preferences don't exist yet, which is fine
        if (error.response && error.response.status !== 404) {
          toast.error('Failed to load preferences');
        }
      } finally {
        setFetchingPreferences(false);
      }
    };

    fetchUserPreferences();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // For numeric fields, ensure we're setting numbers
    if (name === 'cache_ttl' || name === 'max_cache') {
      const numValue = parseInt(value, 10);

      // Apply constraints
      if (name === 'cache_ttl') {
        setPreferences(prev => ({ ...prev, [name]: Math.min(Math.max(0, numValue), 3600) }));
      } else if (name === 'max_cache') {
        setPreferences(prev => ({ ...prev, [name]: Math.min(Math.max(0, numValue), 15) }));
      }
    } else {
      setPreferences(prev => ({ ...prev, [name]: value }));
    }
  };

  const savePreferences = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userId = getUserId();
      if (!userId) {
        toast.error('User ID not found in session');
        setLoading(false);
        return;
      }

      await axios.post(buildApiUrl('SQL', '/api/user/user_preferences'), {
        user_id: userId,
        llm_model: preferences.llm_model,
        cache_ttl: parseInt(preferences.cache_ttl, 10),
        max_cache: parseInt(preferences.max_cache, 10),
        api_key: preferences.api_key,
        llm_provider: preferences.llm_provider,
        host_provider: preferences.host_provider
      });

      toast.success('Preferences saved successfully');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingPreferences) {
    return <div className="p-6 flex justify-center">Loading preferences...</div>;
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* <h2 className="text-2xl font-bold mb-6 text-primary">User Preferences</h2> */}

      <form onSubmit={savePreferences} className="space-y-6">
        <div className="space-y-4">
          {/* LLM Model Selection */}
          <div>
            <label htmlFor="llm_model" className="block text-sm font-medium text-gray-700 mb-1">
              LLM Model
            </label>
            <select
              id="llm_model"
              name="llm_model"
              value={preferences.llm_model}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            >
              {llmOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="llm_model" className="block text-sm font-medium text-gray-700 mb-1">
              LLM Provider
            </label>
            <select
              id="llm_provider"
              name="llm_provider"
              value={preferences.llm_provider}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            >
              {llmProviders.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="llm_model" className="block text-sm font-medium text-gray-700 mb-1">
              Host Provider
            </label>
            <select
              id="host_provider"
              name="host_provider"
              value={preferences.host_provider}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            >
              {hostProviders.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Cache TTL */}
          <div>
            <label htmlFor="cache_ttl" className="block text-sm font-medium text-gray-700 mb-1">
              Cache TTL (seconds, max 3600)
            </label>
            <input
              type="number"
              id="cache_ttl"
              name="cache_ttl"
              value={preferences.cache_ttl}
              onChange={handleInputChange}
              min="0"
              max="3600"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>

          {/* Max Cache */}
          <div>
            <label htmlFor="max_cache" className="block text-sm font-medium text-gray-700 mb-1">
              Max Cache (max 15)
            </label>
            <input
              type="number"
              id="max_cache"
              name="max_cache"
              value={preferences.max_cache}
              onChange={handleInputChange}
              min="0"
              max="15"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>

          {/* API Key */}
          <div>
            <label htmlFor="api_key" className="block text-sm font-medium text-gray-700 mb-1">
              API Key
            </label>
            <input
              type="password"
              id="api_key"
              name="api_key"
              value={preferences.api_key}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primaryDark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserPreferencesSettings;